import { mapActions, mapGetters } from "vuex";

import {
    PAYMENT_RESULT_BASE_PATHS,
    buildPaymentOpeningRoute,
    getPaymentLink,
    isPaymentLinkFresh,
    savePaymentLink
} from "@/common/lib/payment-link";

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 20;

const CARD_VARIANTS = {
    checking: "checking",
    redirecting: "redirecting",
    paid: "success",
    rejected: "rejected",
    unavailable: "rejected",
    pending: "pending"
};

const CARD_TEXTS = {
    checking: {
        title: "Confirmando tu pago…",
        description: "Esto puede tardar unos segundos. No cierres esta página."
    },
    redirecting: {
        title: "Redirigiendo a la pasarela de pago…",
        description: "No cierres esta página."
    },
    paid: {
        title: "Pago confirmado",
        description: "Tu plan ya quedó activo."
    },
    rejected: {
        title: "Tu pago fue rechazado",
        description: "Puedes intentarlo de nuevo."
    },
    pending: {
        title: "Tu pago está pendiente",
        description:
            "Si ya pagaste, espera un momento y vuelve a revisar. Si no, puedes retomar el pago."
    },
    unavailable: {
        title: "Este pago ya no está disponible",
        description: "Genera un pago nuevo para continuar."
    }
};

// Las páginas que usan este mixin definen `resultSource` (onboarding |
// my_plan) y el método `handlePaid`.
const platformPaymentResult = {
    data: () => ({
        state: "checking",
        attempts: 0,
        timer: null
    }),

    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        invoiceId() {
            return this.$route.params.invoiceId;
        },
        cardVariant() {
            return CARD_VARIANTS[this.state];
        },
        cardTitle() {
            return CARD_TEXTS[this.state].title;
        },
        cardDescription() {
            return CARD_TEXTS[this.state].description;
        },
        hasActions() {
            return this.state !== "checking" && this.state !== "redirecting";
        },
        canResumePayment() {
            return this.state === "rejected" || this.state === "pending";
        },
        resumeLabel() {
            return this.state === "rejected"
                ? "Reintentar pago"
                : "Retomar pago";
        }
    },

    watch: {
        "$route.query.mode"(mode) {
            if (mode === "opening") this.openGateway();
        }
    },

    mounted() {
        window.addEventListener("pageshow", this.onPageShow);
        if (this.$route.query.mode === "opening") {
            this.openGateway();
            return;
        }
        this.startChecking();
    },

    beforeDestroy() {
        window.removeEventListener("pageshow", this.onPageShow);
        clearTimeout(this.timer);
    },

    methods: {
        ...mapActions("platformSubscriptions", [
            "getPlatformInvoiceStatus",
            "payPlatformInvoice"
        ]),

        // El navegador restauró la página desde bfcache sin ejecutar el
        // código: se vuelve a la vista de estado en vez de quedar congelada.
        onPageShow(event) {
            if (event.persisted && this.state !== "paid") {
                this.startChecking();
            }
        },

        async openGateway() {
            clearTimeout(this.timer);
            const link = getPaymentLink(this.invoiceId);
            if (!link) {
                await this.replaceMode("result");
                this.startChecking();
                return;
            }
            this.state = "redirecting";
            await this.replaceMode("result");
            window.location.assign(link.url);
        },

        async replaceMode(mode) {
            try {
                await this.$router.replace({
                    path: this.$route.path,
                    query: { ...this.$route.query, mode }
                });
            } catch {
                // Navegación duplicada: la URL ya tenía ese mode.
            }
        },

        startChecking() {
            clearTimeout(this.timer);
            this.state = "checking";
            this.attempts = 0;
            this.check();
        },

        async check() {
            this.attempts += 1;
            let result = null;
            try {
                result = await this.getPlatformInvoiceStatus({
                    id: this.invoiceId
                });
            } catch {
                // Se reintenta igual: el webhook puede tardar.
            }

            if (result && result.paid) {
                await this.handlePaid();
                return;
            }
            if (
                result &&
                result.source &&
                result.source !== this.resultSource &&
                PAYMENT_RESULT_BASE_PATHS[result.source]
            ) {
                this.$router.replace(
                    `${PAYMENT_RESULT_BASE_PATHS[result.source]}/${
                        this.invoiceId
                    }`
                );
                return;
            }
            if (result && result.status === "void") {
                this.state = "unavailable";
                return;
            }
            if (result && result.paymentStatus === "rejected") {
                this.state = "rejected";
                return;
            }
            if (this.attempts >= MAX_ATTEMPTS) {
                this.state = "pending";
                return;
            }
            this.timer = setTimeout(this.check, POLL_INTERVAL_MS);
        },

        async resumePayment() {
            if (isPaymentLinkFresh(getPaymentLink(this.invoiceId))) {
                this.$router.replace(
                    buildPaymentOpeningRoute(this.resultSource, this.invoiceId)
                );
                return;
            }
            try {
                const { paymentUrl } = await this.$loading.wrap(() =>
                    this.payPlatformInvoice({ id: this.invoiceId })
                );
                if (!paymentUrl) {
                    await this.showError({
                        title: "No pudimos generar el pago",
                        message: "Intenta de nuevo en unos minutos.",
                        icon: "error"
                    });
                    return;
                }
                savePaymentLink(this.invoiceId, paymentUrl);
                this.$router.replace(
                    buildPaymentOpeningRoute(this.resultSource, this.invoiceId)
                );
            } catch (error) {
                await this.showError(error);
            }
        },

        async showError(error) {
            this.$sound.play("error");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: error.title,
                text: error.message,
                icon: error.icon
            });
        }
    }
};

export default platformPaymentResult;
